import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getCategoryName } from '../utils/projectNeedsMapping';
import AllocateRemainingModal from './AllocateRemainingModal';

// ─── Service category IDs — use quotesheet-based weighting ──────────────────
const SERVICE_CATEGORY_IDS = new Set([
  'roofing', 'hvac', 'plumbing', 'electrical', 'painting', 'flooring',
  'windows-doors', 'fencing', 'landscaping', 'pest-control', 'pool-installation',
  'solar-panels', 'home-purchase', 'home-refinance', 'generators',
  'remodeling', 'kitchen-remodel', 'bathroom-remodel', 'deck-patio',
  'insulation', 'gutters', 'siding', 'drywall', 'concrete', 'masonry'
]);

// ─── Quotesheet field category buckets for service templates ─────────────────
const SERVICE_PRICING_IDS = ['totalCost', 'laborCost', 'materialCost', 'totalAmount', 'priceMax', 'price', 'budget', 'cost', 'estimatedCost', 'projectCost'];
const SERVICE_TIMING_IDS = ['timeline', 'completionTimeline', 'projectTimeline', 'startDate', 'availability', 'responseTime', 'urgency'];
const SERVICE_WARRANTY_IDS = ['warranty', 'guarantee', 'warrantySupport', 'workmanshipWarranty', 'materialWarranty'];
const SERVICE_QUALITY_IDS = ['materialQuality', 'paintQuality', 'roofingMaterial', 'flooringMaterial', 'fenceMaterial', 'systemType', 'serviceType', 'projectType', 'durability', 'efficiency', 'brandReputation'];
const SERVICE_VENDOR_IDS = ['experience', 'yearsExperience', 'licensing', 'certifications', 'licenseNumber', 'insurance', 'reviews', 'reputation'];

// ─── Weighting Templates ─────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: 'budget-friendly',
    name: 'Budget Friendly',
    icon: 'DollarSign',
    description: '50% price, rest evenly split',
    buildWeights: (needs, isService) => {
      const priceIds = isService
        ? SERVICE_PRICING_IDS
        : ['priceMax', 'price', 'budget', 'totalCost', 'purchasePrice', 'downPayment', 'currentLoanBalance', 'monthlyElectricBill', 'monthlyFee'];
      let priceNeeds = needs?.filter(n => priceIds?.includes(n?.id));
      if (!priceNeeds?.length) {
        priceNeeds = needs?.filter(n => {
          const idLower = (n?.id || '')?.toLowerCase();
          const labelLower = (n?.label || n?.name || '')?.toLowerCase();
          return idLower?.includes('price') || idLower?.includes('budget') || idLower?.includes('cost') ||
                 labelLower?.includes('price') || labelLower?.includes('budget') || labelLower?.includes('cost');
        });
      }
      const priceNeedIds = new Set(priceNeeds?.map(n => n?.id));
      const otherNeeds = needs?.filter(n => !priceNeedIds?.has(n?.id));
      const priceCount = priceNeeds?.length;
      const otherCount = otherNeeds?.length;
      const priceWeight = priceCount > 0 ? 50 / priceCount : 0;
      const otherWeight = otherCount > 0 ? (priceCount > 0 ? 50 : 100) / otherCount : 0;
      const w = {};
      needs?.forEach(n => {
        if (priceNeedIds?.has(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'review-hawk',
    name: 'Review Hawk',
    icon: 'Star',
    description: '40% reviews, 20% price, rest evenly',
    buildWeights: (needs, isService) => {
      const reviewIds = ['reviews', 'reputation', 'vendorRating'];
      const priceIds = isService
        ? SERVICE_PRICING_IDS
        : ['priceMax', 'price', 'budget', 'totalCost', 'purchasePrice', 'downPayment', 'currentLoanBalance', 'monthlyElectricBill', 'monthlyFee'];
      const reviewNeeds = needs?.filter(n => reviewIds?.includes(n?.id));
      let priceNeeds = needs?.filter(n => priceIds?.includes(n?.id));
      const otherNeeds = needs?.filter(n => !reviewIds?.includes(n?.id) && !priceIds?.includes(n?.id));
      const reviewWeight = reviewNeeds?.length > 0 ? 40 / reviewNeeds?.length : 0;
      const priceWeight = priceNeeds?.length > 0 ? 20 / priceNeeds?.length : 0;
      const remaining = 100 - (reviewNeeds?.length * reviewWeight) - (priceNeeds?.length * priceWeight);
      const otherWeight = otherNeeds?.length > 0 ? remaining / otherNeeds?.length : 0;
      const w = {};
      needs?.forEach(n => {
        if (reviewIds?.includes(n?.id)) w[n.id] = reviewWeight?.toFixed(2);
        else if (priceIds?.includes(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'quality-first',
    name: 'Quality First!',
    icon: 'Award',
    description: (isService) => isService ? '40% quality/materials, 20% price, rest evenly' : '40% quality/specs, 20% price, rest evenly',
    buildWeights: (needs, isService) => {
      const qualityIds = isService
        ? SERVICE_QUALITY_IDS
        : ['material', 'materialPreference', 'roofingMaterial', 'flooringMaterial', 'fenceMaterial', 'mattressType', 'displayType', 'finish', 'colorFinish', 'processor', 'ram', 'resolution', 'firmness', 'systemType', 'fuelType', 'powerOutput', 'operatingSystem', 'connectivity', 'ecosystem', 'keyFeatures'];
      const priceIds = isService
        ? SERVICE_PRICING_IDS
        : ['priceMax', 'price', 'budget', 'totalCost', 'purchasePrice', 'downPayment', 'currentLoanBalance', 'monthlyElectricBill', 'monthlyFee'];
      const qualityNeeds = needs?.filter(n => qualityIds?.includes(n?.id));
      let priceNeeds = needs?.filter(n => priceIds?.includes(n?.id));
      const effectiveQuality = qualityNeeds?.length > 0 ? qualityNeeds : needs?.filter(n => n?.priority === 'High' && !priceIds?.includes(n?.id));
      const effectiveOther = needs?.filter(n => !effectiveQuality?.find(q => q?.id === n?.id) && !priceIds?.includes(n?.id));
      const qualityWeight = effectiveQuality?.length > 0 ? 40 / effectiveQuality?.length : 0;
      const priceWeight = priceNeeds?.length > 0 ? 20 / priceNeeds?.length : 0;
      const remaining = 100 - (effectiveQuality?.length * qualityWeight) - (priceNeeds?.length * priceWeight);
      const otherWeight = effectiveOther?.length > 0 ? remaining / effectiveOther?.length : 0;
      const w = {};
      needs?.forEach(n => {
        if (effectiveQuality?.find(q => q?.id === n?.id)) w[n.id] = qualityWeight?.toFixed(2);
        else if (priceIds?.includes(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'fastest-completion',
    name: 'Fastest Completion',
    icon: 'Clock',
    description: '40% timing, 30% price, rest evenly',
    serviceOnly: true,
    buildWeights: (needs, isService) => {
      if (!isService) return null;
      const timingNeeds = needs?.filter(n => SERVICE_TIMING_IDS?.includes(n?.id));
      let priceNeeds = needs?.filter(n => SERVICE_PRICING_IDS?.includes(n?.id));
      const otherNeeds = needs?.filter(n => !SERVICE_TIMING_IDS?.includes(n?.id) && !SERVICE_PRICING_IDS?.includes(n?.id));
      const timingWeight = timingNeeds?.length > 0 ? 40 / timingNeeds?.length : 0;
      const priceWeight = priceNeeds?.length > 0 ? 30 / priceNeeds?.length : 0;
      const remaining = 100 - (timingNeeds?.length * timingWeight) - (priceNeeds?.length * priceWeight);
      const otherWeight = otherNeeds?.length > 0 ? remaining / otherNeeds?.length : 0;
      const w = {};
      needs?.forEach(n => {
        if (SERVICE_TIMING_IDS?.includes(n?.id)) w[n.id] = timingWeight?.toFixed(2);
        else if (SERVICE_PRICING_IDS?.includes(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'best-warranty',
    name: 'Best Warranty',
    icon: 'Shield',
    description: '40% warranty, 30% price, rest evenly',
    serviceOnly: true,
    buildWeights: (needs, isService) => {
      if (!isService) return null;
      const warrantyNeeds = needs?.filter(n => SERVICE_WARRANTY_IDS?.includes(n?.id));
      let priceNeeds = needs?.filter(n => SERVICE_PRICING_IDS?.includes(n?.id));
      const otherNeeds = needs?.filter(n => !SERVICE_WARRANTY_IDS?.includes(n?.id) && !SERVICE_PRICING_IDS?.includes(n?.id));
      const warrantyWeight = warrantyNeeds?.length > 0 ? 40 / warrantyNeeds?.length : 0;
      const priceWeight = priceNeeds?.length > 0 ? 30 / priceNeeds?.length : 0;
      const remaining = 100 - (warrantyNeeds?.length * warrantyWeight) - (priceNeeds?.length * priceWeight);
      const otherWeight = otherNeeds?.length > 0 ? remaining / otherNeeds?.length : 0;
      const w = {};
      needs?.forEach(n => {
        if (SERVICE_WARRANTY_IDS?.includes(n?.id)) w[n.id] = warrantyWeight?.toFixed(2);
        else if (SERVICE_PRICING_IDS?.includes(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'trusted-vendor',
    name: 'Trusted Vendor',
    icon: 'BadgeCheck',
    description: '40% experience/licensing, 30% price, rest evenly',
    serviceOnly: true,
    buildWeights: (needs, isService) => {
      if (!isService) return null;
      const vendorNeeds = needs?.filter(n => SERVICE_VENDOR_IDS?.includes(n?.id));
      let priceNeeds = needs?.filter(n => SERVICE_PRICING_IDS?.includes(n?.id));
      const otherNeeds = needs?.filter(n => !SERVICE_VENDOR_IDS?.includes(n?.id) && !SERVICE_PRICING_IDS?.includes(n?.id));
      const vendorWeight = vendorNeeds?.length > 0 ? 40 / vendorNeeds?.length : 0;
      const priceWeight = priceNeeds?.length > 0 ? 30 / priceNeeds?.length : 0;
      const remaining = 100 - (vendorNeeds?.length * vendorWeight) - (priceNeeds?.length * priceWeight);
      const otherWeight = otherNeeds?.length > 0 ? remaining / otherNeeds?.length : 0;
      const w = {};
      needs?.forEach(n => {
        if (SERVICE_VENDOR_IDS?.includes(n?.id)) w[n.id] = vendorWeight?.toFixed(2);
        else if (SERVICE_PRICING_IDS?.includes(n?.id)) w[n.id] = priceWeight?.toFixed(2);
        else w[n.id] = otherWeight?.toFixed(2);
      });
      const total = Object.values(w)?.reduce((s, v) => s + parseFloat(v), 0);
      const diff = (100 - total)?.toFixed(2);
      if (Math.abs(parseFloat(diff)) > 0.001) {
        const lastKey = needs?.[needs?.length - 1]?.id;
        w[lastKey] = (parseFloat(w?.[lastKey]) + parseFloat(diff))?.toFixed(2);
      }
      return w;
    }
  },
  {
    id: 'balanced',
    name: 'Balanced',
    icon: 'Scale',
    description: 'Equal weight across all needs',
    buildWeights: (needs) => {
      const even = (100 / needs?.length);
      const w = {};
      needs?.forEach((n, i) => {
        if (i === needs?.length - 1) {
          const soFar = (needs?.length - 1) * parseFloat(even?.toFixed(2));
          w[n.id] = (100 - soFar)?.toFixed(2);
        } else {
          w[n.id] = even?.toFixed(2);
        }
      });
      return w;
    }
  }
];

const WeightingSection = ({ projectNeeds, purchaseType, onWeightsComplete, initialWeights = {} }) => {
  const [weights, setWeights] = useState(initialWeights);
  const [isComplete, setIsComplete] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  // Start collapsed if initialWeights already has values (restored from Supabase)
  const hasRestoredWeights = Object.keys(initialWeights)?.length > 0;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  // Detect if this is a service-based category
  const isServiceCategory = SERVICE_CATEGORY_IDS?.has(purchaseType);

  // Filter templates: show service-only templates only for service categories
  const visibleTemplates = TEMPLATES?.filter(t => {
    if (t?.serviceOnly) return isServiceCategory;
    return true;
  });

  // Sync weights when initialWeights changes (e.g. loaded from Supabase after mount)
  useEffect(() => {
    if (Object.keys(initialWeights)?.length > 0) {
      setWeights(initialWeights);
      setIsExpanded(false);
    }
  }, [JSON.stringify(initialWeights)]);

  // Initialize weights if not provided
  useEffect(() => {
    if (Object.keys(initialWeights)?.length === 0 && projectNeeds?.length > 0) {
      const needIds = projectNeeds?.map(n => n?.id) || [];
      setWeights(prev => {
        const alreadyInitialized = needIds?.length > 0 && needIds?.every(id => id in prev && prev?.[id] !== '');
        if (alreadyInitialized) return prev;
        // Apply balanced template by default: equal weight across all needs
        const balancedTemplate = TEMPLATES?.find(t => t?.id === 'balanced');
        return balancedTemplate ? balancedTemplate?.buildWeights(projectNeeds, isServiceCategory) : prev;
      });
      setActiveTemplate(prev => prev === null ? 'balanced' : prev);
    }
  }, [projectNeeds, initialWeights, isServiceCategory]);

  // Check if all weights are filled and sum to 100
  useEffect(() => {
    if (!projectNeeds || projectNeeds?.length === 0) {
      setIsComplete(false);
      return;
    }
    const allFilled = projectNeeds?.every(need => {
      const val = weights?.[need?.id];
      return val !== undefined && val !== null && val !== '';
    });
    const total = Object.values(weights)?.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const roundedTotal = Math.round(total * 100) / 100;
    const isValid = allFilled && Math.abs(roundedTotal - 100) < 0.01;
    setIsComplete(isValid);
  }, [weights, projectNeeds]);

  const handleWeightChange = (needId, value) => {
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setWeights(prev => ({ ...prev, [needId]: value }));
      // Deselect template on manual change
      setActiveTemplate(null);
    }
  };

  const handleApplyTemplate = (template) => {
    if (!projectNeeds || projectNeeds?.length === 0) return;
    const newWeights = template?.buildWeights(projectNeeds, isServiceCategory);
    if (!newWeights) return; // serviceOnly template on non-service category
    setWeights(newWeights);
    setActiveTemplate(template?.id);
    setShowPrompt(false);
  };

  const calculateTotal = () => {
    return Object.values(weights)?.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleApplyWeights = () => {
    const total = calculateTotal();
    if (Math.abs(total - 100) > 0.01) {
      setShowPrompt(true);
      return;
    }
    
    const allFilled = projectNeeds?.every(need => {
      const val = weights?.[need?.id];
      return val !== undefined && val !== null && val !== '';
    });
    if (!allFilled) {
      setShowPrompt(true);
      return;
    }

    const numericWeights = {};
    Object.keys(weights)?.forEach(key => {
      numericWeights[key] = parseFloat(weights?.[key]);
    });

    onWeightsComplete?.(numericWeights);
    setIsExpanded(false);
  };

  const handleDistributeEvenly = () => {
    const evenWeight = (100 / projectNeeds?.length)?.toFixed(2);
    const newWeights = {};
    projectNeeds?.forEach((need, index) => {
      if (index === projectNeeds?.length - 1) {
        const currentTotal = (projectNeeds?.length - 1) * parseFloat(evenWeight);
        newWeights[need?.id] = (100 - currentTotal)?.toFixed(2);
      } else {
        newWeights[need?.id] = evenWeight;
      }
    });
    setWeights(newWeights);
    setActiveTemplate('balanced');
  };

  const handleAllocateRemaining = (needId) => {
    const remaining = 100 - calculateTotal();
    if (remaining > 0) {
      setWeights(prev => ({
        ...prev,
        [needId]: (parseFloat(prev?.[needId] || 0) + remaining)?.toFixed(2)
      }));
    }
    setShowAllocateModal(false);
  };

  const total = calculateTotal();
  const isValidTotal = Math.abs(total - 100) < 0.01;
  const allFilled = projectNeeds?.every(need => weights?.[need?.id] && weights?.[need?.id] !== '');
  const categoryName = getCategoryName(purchaseType);

  // ── Service category weight category labels for display ───────────────────
  const getWeightCategory = (needId) => {
    if (!isServiceCategory) return null;
    if (SERVICE_PRICING_IDS?.includes(needId)) return { label: 'Pricing', color: '#15803d', bg: '#f0fdf4' };
    if (SERVICE_TIMING_IDS?.includes(needId)) return { label: 'Timing', color: '#1d4ed8', bg: '#eff6ff' };
    if (SERVICE_WARRANTY_IDS?.includes(needId)) return { label: 'Warranty', color: '#7c3aed', bg: '#f5f3ff' };
    if (SERVICE_QUALITY_IDS?.includes(needId)) return { label: 'Quality', color: '#b45309', bg: '#fffbeb' };
    if (SERVICE_VENDOR_IDS?.includes(needId)) return { label: 'Vendor', color: '#0e7490', bg: '#ecfeff' };
    return null;
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden mb-6" style={{ border: '1.5px solid #fde8d8', boxShadow: '0 2px 12px rgba(245,138,84,0.07)' }}>
      {/* Header */}
      <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', borderBottom: '1.5px solid #fde8d8' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
              <Icon name="Sliders" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Adjust Importance Weighting</h3>
              <p className="text-sm text-gray-500">
                Assign percentage weights to each {categoryName} need (must total 100%)
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {!isExpanded && (
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle2" size={20} className="text-green-600" />
                <span className="text-sm font-medium text-green-700">Weights Applied</span>
              </div>
            )}
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="outline"
              size="sm"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        </div>
      </div>
      {/* Weighting Form */}
      {isExpanded && (
        <div className="p-6">

          {/* ── Service Category Legend ── */}
          {isServiceCategory && (
            <div className="mb-5 p-4 rounded-xl" style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Info" size={14} className="text-blue-500" />
                <span className="text-xs font-semibold text-gray-700">Quote categories — weight by what matters most to you</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Pricing', color: '#15803d', bg: '#f0fdf4' },
                  { label: 'Timing', color: '#1d4ed8', bg: '#eff6ff' },
                  { label: 'Warranty', color: '#7c3aed', bg: '#f5f3ff' },
                  { label: 'Quality', color: '#b45309', bg: '#fffbeb' },
                  { label: 'Vendor', color: '#0e7490', bg: '#ecfeff' },
                ]?.map(cat => (
                  <span key={cat?.label} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: cat?.bg, color: cat?.color }}>
                    {cat?.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Weighting Templates ── */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Sparkles" size={16} className="text-orange-400" />
              <span className="text-sm font-semibold text-gray-700">Quick Templates</span>
              <span className="text-xs text-gray-400">— pick a preset or customize below</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {visibleTemplates?.map((tpl) => {
                const isActive = activeTemplate === tpl?.id;
                const description = typeof tpl?.description === 'function' ? tpl?.description(isServiceCategory) : tpl?.description;
                return (
                  <button
                    key={tpl?.id}
                    onClick={() => handleApplyTemplate(tpl)}
                    className="relative flex flex-col items-center text-center p-3 rounded-2xl transition-all duration-200 focus:outline-none"
                    style={isActive ? {
                      background: 'linear-gradient(135deg, #F58A54, #F06F6B)',
                      border: '2px solid #F58A54',
                      boxShadow: '0 4px 16px rgba(245,138,84,0.35)',
                      transform: 'translateY(-2px)'
                    } : {
                      background: '#fff5f0',
                      border: '1.5px solid #fde8d8',
                      boxShadow: '0 1px 4px rgba(245,138,84,0.08)'
                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = '#F58A54'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245,138,84,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = '#fde8d8'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(245,138,84,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                  >
                    {isActive && (
                      <div className="absolute top-2 right-2">
                        <Icon name="CheckCircle2" size={13} color="white" />
                      </div>
                    )}
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center mb-2 flex-shrink-0"
                      style={isActive
                        ? { background: 'rgba(255,255,255,0.25)' }
                        : { background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }
                      }
                    >
                      <Icon name={tpl?.icon} size={18} color="white" />
                    </div>
                    <span
                      className="text-xs font-bold leading-tight mb-1"
                      style={{ color: isActive ? 'white' : '#1f2937' }}
                    >
                      {tpl?.name}
                    </span>
                    <span
                      className="text-xs leading-tight"
                      style={{ color: isActive ? 'rgba(255,255,255,0.85)' : '#6b7280' }}
                    >
                      {description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prompt Message */}
          {showPrompt && !isComplete && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-1">Complete Weighting Required</h4>
                  <p className="text-sm text-yellow-800">
                    {!allFilled 
                      ? 'Please assign a percentage weight to all needs before proceeding.'
                      : `Total must equal 100%. Current total: ${total?.toFixed(2)}%`
                    }
                  </p>
                </div>
                <button onClick={() => setShowPrompt(false)} className="text-yellow-600 hover:text-yellow-800">
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Quick Action */}
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Icon name="Info" size={14} />
              Assign higher percentages to more important needs
            </div>
            <Button
              onClick={handleDistributeEvenly}
              variant="outline"
              size="sm"
              iconName="Percent"
              iconPosition="left"
            >
              Distribute Evenly
            </Button>
          </div>

          {/* Weighting Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {projectNeeds?.map((need) => {
              const weightCat = getWeightCategory(need?.id);
              return (
                <div key={need?.id} className="rounded-xl p-4 transition-all hover:shadow-sm" style={{ border: '1.5px solid #fde8d8', background: 'white' }}>
                  <div className="flex items-start space-x-3">
                    <div
                      className="p-2 rounded-xl flex-shrink-0"
                      style={{
                        background: need?.priority === 'High' ?'linear-gradient(135deg, #F58A54, #F06F6B)'
                          : need?.priority === 'Medium' ?'linear-gradient(135deg, #F06F6B, #E0528A)' :'linear-gradient(135deg, #a78bfa, #818cf8)'
                      }}
                    >
                      <Icon name={need?.icon} size={16} color="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="font-semibold text-gray-900">{need?.name}</h4>
                        {weightCat && (
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: weightCat?.bg, color: weightCat?.color }}>
                            {weightCat?.label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">{need?.description}</p>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={weights?.[need?.id] || ''}
                            onChange={(e) => handleWeightChange(need?.id, e?.target?.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 rounded-xl outline-none text-right pr-8 transition-all"
                            style={{ border: '1.5px solid #fde8d8', background: '#fff5f0' }}
                            onFocus={e => e.target.style.borderColor = '#F58A54'}
                            onBlur={e => e.target.style.borderColor = '#fde8d8'}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">%</span>
                        </div>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{
                            background: need?.priority === 'High' ? '#fff5f0' : need?.priority === 'Medium' ? '#fff0f5' : '#f5f0ff',
                            color: need?.priority === 'High' ? '#F58A54' : need?.priority === 'Medium' ? '#E0528A' : '#7c3aed',
                          }}
                        >
                          {need?.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total and Actions */}
          <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', border: '1.5px solid #fde8d8' }}>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-semibold">Total Weight:</span>
              </div>
              <div className={`text-2xl font-bold ${
                isValidTotal ? 'text-green-600' : total > 100 ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {total?.toFixed(2)}%
              </div>
              {isValidTotal && allFilled && (
                <div className="flex items-center space-x-1 text-green-600">
                  <Icon name="CheckCircle2" size={20} />
                  <span className="text-sm font-medium">Ready</span>
                </div>
              )}
              {!isValidTotal && total !== 0 && total < 100 && (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 text-yellow-600">
                    <Icon name="AlertCircle" size={20} />
                    <span className="text-sm font-medium">
                      {(100 - total)?.toFixed(2)}% remaining
                    </span>
                  </div>
                  <Button
                    onClick={() => setShowAllocateModal(true)}
                    size="sm"
                    variant="outline"
                    iconName="Plus"
                    iconPosition="left"
                    className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                  >
                    Allocate
                  </Button>
                </div>
              )}
              {!isValidTotal && total > 100 && (
                <div className="flex items-center space-x-1 text-red-600">
                  <Icon name="AlertCircle" size={20} />
                  <span className="text-sm font-medium">
                    {(total - 100)?.toFixed(2)}% over
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={handleApplyWeights}
              disabled={!isComplete}
              size="lg"
              iconName="Check"
              iconPosition="left"
              className={isComplete ? '' : 'bg-gray-400 cursor-not-allowed'}
              style={isComplete ? { background: 'linear-gradient(135deg, #F58A54, #F06F6B)', color: 'white', border: 'none' } : {}}
            >
              Apply Weights & Calculate
            </Button>
          </div>
        </div>
      )}
      {/* Allocate Remaining Modal */}
      <AllocateRemainingModal
        isOpen={showAllocateModal}
        onClose={() => setShowAllocateModal(false)}
        projectNeeds={projectNeeds}
        weights={weights}
        remainingPercentage={100 - total}
        onAllocate={handleAllocateRemaining}
      />
    </div>
  );
};

export default WeightingSection;